import React from 'react';

import clsx from 'clsx';
import makeStyles from '@mui/styles/makeStyles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import MenuIcon from '@mui/icons-material/Menu';
import TextField from '@mui/material/TextField';
import history from '../utilities/history';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import moment from 'moment';
import constants from '../utilities/constants';
import firebase from '../utilities/firebase';
import algolia from '../utilities/algolia';
import { v4 as uuidv4 } from 'uuid';
import { StateManager } from '../utilities/stateManager';
import ResultsList from './ResultsList';
import AddCarDialog from './AddCarDialog';
import AddServiceOrderDialog from './AddServiceOrderDialog';
import AddCustomerDialog from './AddCustomerDialog';
import AddLeadDialog from './AddLeadDialog';
import BulkPurchaseModal from './BulkPurchaseModal';
import BulkIncentiveModal from './BulkIncentiveModal';
import CreateLabelDialog from './CarModal/CreateLabelDialog';
import {
  ListItemIcon,
  ListItemText,
  Box,
  Paper,
  Button,
  IconButton,
  Badge,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Bolt as BoltIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { menuSections, userSection, renderMenuSections } from '../config/menuItems';
import Store from '../utilities/store';
import { signOutDeliberately } from '../utilities/session';
import NotificationTray from './NotificationTray';

export default function Header(props) {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [anchor, setAnchor] = React.useState(null);
    const [results, setResults] = React.useState([]);
    const [selected, setSelected] = React.useState(0);
    const [searching, setSearching] = React.useState(false);
    const [focused, setFocused] = React.useState(false);
    // On mobile the search collapses to an icon and expands to fill the header
    // when tapped. `mobileExpanded` tracks that expanded state.
    const [mobileExpanded, setMobileExpanded] = React.useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const searchInputRef = React.useRef(null);
    const searchIdRef = React.useRef(0);
    const debounceRef = React.useRef(null);
    const [isAddCarDialogOpen, setIsAddCarDialogOpen] = React.useState(false);
    const [isAddServiceOrderDialogOpen, setIsAddServiceOrderDialogOpen] = React.useState(false);
    const [isAddCustomerDialogOpen, setIsAddCustomerDialogOpen] = React.useState(false);
    const [isAddLeadDialogOpen, setIsAddLeadDialogOpen] = React.useState(false);
    const [isBulkPurchaseModalOpen, setIsBulkPurchaseModalOpen] = React.useState(false);
    const [isBulkIncentiveModalOpen, setIsBulkIncentiveModalOpen] = React.useState(false);
    const [isShippingLabelDialogOpen, setIsShippingLabelDialogOpen] = React.useState(false);
    const [newSOCount, setNewSOCount] = React.useState(0);
    const [newLeadsCount, setNewLeadsCount] = React.useState(0);
    const [leadFollowupCount, setLeadFollowupCount] = React.useState(0);
    const [phoneUnreadCount, setPhoneUnreadCount] = React.useState(
      () => StateManager.phoneUnreadCount || 0
    );

    // Live count of service orders awaiting review (status "new"), shown as a
    // badge on the Service Pipeline menu item.
    React.useEffect(() => {
        const unsub = Store.subscribeNewServiceOrderCount(setNewSOCount);
        return () => unsub && unsub();
    }, []);

    // Unread shop-line texts. PhoneInboundAlerts owns the listener and pushes
    // the count here so the Phone row (and the menu button) stay badged even
    // when browser notifications are denied.
    React.useEffect(() => {
        StateManager.setPhoneUnreadCount = setPhoneUnreadCount;
        return () => {
            if (StateManager.setPhoneUnreadCount === setPhoneUnreadCount) {
                StateManager.setPhoneUnreadCount = undefined;
            }
        };
    }, []);

    // Live lead badges: new (awaiting first contact) on the Leads item, and
    // due follow-ups on the Rep Lead Performance item. Scoped to the current rep
    // unless they're backoffice/admin.
    React.useEffect(() => {
        const scope = { userID: StateManager.userID, isBackoffice: StateManager.isBackoffice() || StateManager.isAdmin() };
        const unsubNew = Store.subscribeNewLeadsCount(setNewLeadsCount, scope);
        const unsubFollow = Store.subscribeLeadsNeedingFollowupCount(setLeadFollowupCount, scope);
        return () => { unsubNew && unsubNew(); unsubFollow && unsubFollow(); };
    }, []);

    // Check for URL parameters to trigger dialogs
    // Usage: Add ?openAddLead=true to any URL to automatically open the Add Lead dialog
    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const openAddLead = urlParams.get('openAddLead');

        if (openAddLead === 'true') {
            setIsAddLeadDialogOpen(true);
            const url = new URL(window.location);
            url.searchParams.delete('openAddLead');
            window.history.replaceState({}, '', url);
        }
    }, []);

    // When the mobile search expands, focus the input so it "activates on
    // focus" immediately (the user tapped the icon expecting to type).
    React.useEffect(() => {
      if (mobileExpanded && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [mobileExpanded]);

    // If the viewport grows past mobile while expanded, drop back to the
    // normal desktop layout.
    React.useEffect(() => {
      if (!isMobile && mobileExpanded) setMobileExpanded(false);
    }, [isMobile, mobileExpanded]);

    // Reset the search bar back to empty. Cancels any pending debounce and
    // bumps the search id so an in-flight Algolia response can't repopulate
    // the results after we've cleared.
    const clearSearch = React.useCallback(() => {
      clearTimeout(debounceRef.current);
      searchIdRef.current += 1;
      setSearch('');
      setResults([]);
      setSelected(0);
      setSearching(false);
    }, []);

    const closeMobileSearch = () => {
      setMobileExpanded(false);
      clearSearch();
    };

    // The input blurs on mousedown, before the result row's click fires. Track
    // whether the pointer went down inside the results dropdown so the blur
    // handler doesn't clear (and unmount) the row the user is clicking.
    const pointerInResultsRef = React.useRef(false);
    React.useEffect(() => {
      const onPointerDown = (e) => {
        pointerInResultsRef.current = !!(e.target.closest && e.target.closest('[data-search-results]'));
      };
      const onPointerUp = () => {
        // Defer so the click handler (which runs before this timeout) is unaffected.
        setTimeout(() => { pointerInResultsRef.current = false; }, 0);
      };
      document.addEventListener('mousedown', onPointerDown, true);
      document.addEventListener('touchstart', onPointerDown, true);
      document.addEventListener('mouseup', onPointerUp, true);
      document.addEventListener('touchend', onPointerUp, true);
      return () => {
        document.removeEventListener('mousedown', onPointerDown, true);
        document.removeEventListener('touchstart', onPointerDown, true);
        document.removeEventListener('mouseup', onPointerUp, true);
        document.removeEventListener('touchend', onPointerUp, true);
      };
    }, []);

    const handleSearchBlur = () => {
      setFocused(false);
      if (pointerInResultsRef.current) return;
      clearSearch();
    };

    const hendleOpen = (event) => {
      console.log('open')
      setAnchor(event.currentTarget);
    };

    const handleClose = () => {
      setAnchor(null);
    };

    const updateValue = (e) => {
      const { value } = e.target;
      setSearch(value);
      clearTimeout(debounceRef.current);

      if(value == ""){
        setResults([]);
        setSelected(0);
        setSearching(false);
        return;
      }

      setSearching(true);
      debounceRef.current = setTimeout(async () => {
      const searchId = ++searchIdRef.current;
      const indicies = ["cars", "orders", "customers"];
      const queries = indicies.map(name => ({
        indexName: name, 
        query: value,
        hitsPerPage: 20 // Get up to 100 results for instant search
      }));
      const { results } = await algolia.client.multipleQueries(queries);
      // console.log(results)

      let hits = [];
      indicies.forEach((type, i) => {
        const cur_hits = results[i].hits.map(hit => ({
          ...hit,
          type: type,
          action: getAction(hit, type),
          label: getLabel(hit, type)
        }));
        hits = [...hits, cur_hits];
      });

      // Preserve Algolia's ranking order (relevance first, newest stock as the
      // tie-breaker via the index `customRanking`). Do NOT re-sort by stock here
      // — that would discard textual relevance and put the highest stock number
      // on top regardless of how well it matches the query.
      hits = hits.flat();

      // Enrich order hits with thumbnail + car title from Firestore.
      // The Algolia orders index doesn't reliably store these, so we fetch
      // the order doc (has thumbnail + car ref) then the car doc (has year/make/model).
      const orderHits = hits.filter(h => h.type === 'orders');
      if (orderHits.length > 0) {
        try {
          // Step 1: fetch order documents to get thumbnail and car reference
          const orderDocs = await Promise.all(
            orderHits.map(h => firebase.firestore().collection('orders').doc(h.objectID).get())
          );
          const orderDataMap = {}; // objectID -> { thumbnail, carId }
          const carIdsNeeded = new Set();
          orderDocs.forEach((doc, idx) => {
            if (doc.exists) {
              const d = doc.data();
              orderDataMap[orderHits[idx].objectID] = {
                thumbnail: d.thumbnail || null,
                carId: d.car || null
              };
              if (d.car) carIdsNeeded.add(d.car);
            }
          });

          // Step 2: fetch car documents to get year/make/model for title
          const uniqueCarIds = [...carIdsNeeded];
          const carDocs = await Promise.all(
            uniqueCarIds.map(id => firebase.firestore().collection('cars').doc(id).get())
          );
          const carTitleMap = {}; // carId -> car_title string
          carDocs.forEach((doc, idx) => {
            if (doc.exists) {
              const d = doc.data();
              carTitleMap[uniqueCarIds[idx]] = [d.year, d.make, d.model].filter(Boolean).join(' ');
            }
          });

          // Step 3: update hits
          hits = hits.map(hit => {
            if (hit.type === 'orders' && orderDataMap[hit.objectID]) {
              const { thumbnail, carId } = orderDataMap[hit.objectID];
              const car_title = carId ? carTitleMap[carId] : null;
              const id = hit.id || hit.objectID || '';
              return {
                ...hit,
                thumbnail: thumbnail || hit.thumbnail,
                label: car_title ? `${id} — ${car_title}` : `Order: ${id}`
              };
            }
            return hit;
          });
        } catch (e) {
          // Proceed with Algolia data if Firestore fetch fails
        }
      }

      // Add "View All Results" as the first item
      if (hits.length > 0) {
        const resultCount = hits.length;
        hits.unshift({
          objectID: 'view-all-results',
          type: 'view-all',
          action: (event) => {
            const url = `/search?q=${encodeURIComponent(value)}&type=cars`;
            if (event?.metaKey || event?.ctrlKey) {
              window.open(url, '_blank');
              return;
            }
            history.push(url);
            clearSearch();
          },
          label: `View All Results (${resultCount})`,
          isViewAll: true
        });
      }

      if (searchId !== searchIdRef.current) return;
      setResults(hits);
      // Highlight the top real result (index 0 is the "View All" row), so
      // pressing Enter opens the best match rather than the search page.
      setSelected(hits.length > 1 ? 1 : 0);
      setSearching(false);
      }, 200);
    }

    const keyPress = (e) => {
      if(e.key === "Enter") {
        const curr = results && results.length > 0 ? results[selected] : undefined;
        if (curr && typeof curr.action === 'function') {
          curr.action();
          clearSearch();
        }
        return;
      }

      if(e.key === "Escape") {
        clearSearch();
        return;
      }

      let newActive = selected;
      if(e.key === "ArrowUp") newActive -=  1;
      if(e.key === "ArrowDown") newActive += 1;
      const maxIndex = Math.max(results.length - 1, 0);
      newActive = Math.max(Math.min(newActive, maxIndex), 0)
      setSelected(newActive);
    }

    const navigate = async (location) => {
      history.push(location);
      handleClose();
    }

    const signOut = async () => {
      // signOutDeliberately, not auth().signOut(): it flags the intent so the auth
      // listeners treat the resulting null as a real sign-out instead of holding it
      // through their grace period (see utilities/authGuard).
      await signOutDeliberately();
      history.go(0);
      handleClose();
    }

    const addServiceOrder = () => {
      setIsAddServiceOrderDialogOpen(true);
      handleClose();
    }

    const addCustomer = () => {
      setIsAddCustomerDialogOpen(true);
      handleClose();
    }

    const handleAction = (action) => {
      switch (action) {
        case 'addCar':
          setIsAddCarDialogOpen(true);
          break;
        case 'addServiceOrder':
          setIsAddServiceOrderDialogOpen(true);
          break;
        case 'addCustomer':
          setIsAddCustomerDialogOpen(true);
          break;
        case 'addLead':
          setIsAddLeadDialogOpen(true);
          break;
        case 'bulkPurchase':
          setIsBulkPurchaseModalOpen(true);
          break;
        case 'bulkIncentive':
          setIsBulkIncentiveModalOpen(true);
          break;
        case 'createShippingLabel':
          setIsShippingLabelDialogOpen(true);
          break;
        case 'signOut':
          signOutDeliberately().then(() => history.go(0));
          break;
        default:
          break;
      }
      handleClose();
    };

    return <>
      <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)} style={{ backgroundColor: '#023e84' }}>
        <Toolbar className={classes.toolbar} style={{ gap: 8 }}>
          {/* Left: logo. On desktop it's an equal-width (flex:1) section so the
              search stays centered; on mobile it collapses to content width so
              the search bar can't render over it. Hidden while the mobile
              search is expanded to give the field the full header width. */}
          {
            !(isMobile && mobileExpanded) &&
            <Box sx={{ flex: { xs: '0 0 auto', md: 1 }, display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <a href={'/'}>
                <img className={classes.logo} src={"/logo-clear.png"} alt="image" />
              </a>
            </Box>
          }
          {/* Center: search. On mobile it's just an icon until tapped, then it
              expands to fill the whole header. On desktop it's the wide,
              centered search bar. */}
          {
            StateManager.authed && isMobile && !mobileExpanded &&
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <IconButton color="inherit" onClick={() => setMobileExpanded(true)} size="large">
                <SearchIcon />
              </IconButton>
            </Box>
          }
          {
            StateManager.authed && !(isMobile && !mobileExpanded) &&
            <Box sx={{ flex: mobileExpanded ? '1 1 auto' : { xs: '1 1 auto', md: '0 1 1040px' }, display: 'flex', alignItems: 'center', minWidth: 0, gap: mobileExpanded ? 0.5 : 0 }}>
              {
                mobileExpanded &&
                <IconButton color="inherit" onClick={closeMobileSearch} size="large">
                  <ArrowBackIcon />
                </IconButton>
              }
              <TextField
                className={classes.title}
                value={search}
                fullWidth
                inputRef={searchInputRef}
                style={{
                  // Keep the root transparent — the rounded InputProps element
                  // below owns the background so the fill matches its radius
                  // (a background here would show as a square behind the pill).
                  backgroundColor: 'transparent',
                  flexGrow: 1,
                }}
                onFocus={() => setFocused(true)}
                onBlur={handleSearchBlur}
                onChange={updateValue}
                onKeyDown={keyPress}
                placeholder={props.title || 'Search for cars, customers, orders...'}
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: focused ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
                    opacity: 1,
                    transition: 'color 0.3s ease'
                  }
                }}
                autoComplete="off"
                InputProps={{
                  style: {
                    borderRadius: '24px',
                    paddingLeft: '16px',
                    backgroundColor: focused ? 'white' : 'rgba(255, 255, 255, 0.1)',
                    border: focused ? '1px solid #e0e0e0' : '1px solid rgba(255, 255, 255, 0.3)',
                    transition: 'all 0.3s ease'
                  },
                  startAdornment: (
                    <SearchIcon
                      style={{
                        marginLeft: '8px',
                        color: focused ? '#666' : 'rgba(255, 255, 255, 0.8)',
                        fontSize: '20px',
                        transition: 'color 0.3s ease'
                      }}
                    />
                  ),
                  endAdornment: (
                    <>
                      {searching && (
                        <CircularProgress size={20} style={{ marginRight: '8px' }} />
                      )}
                      {search && !searching && (
                        <IconButton
                          size="small"
                          onClick={clearSearch}
                          style={{ marginRight: '8px' }}
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </>
                  ),
                }}
              />

            </Box>
          }
          {/* Right: actions grouped together. Equal-width (flex:1) on desktop to
              keep the search centered; content-width on mobile so it doesn't
              crowd the search bar. Hidden while the mobile search is expanded. */}
          {
            !(isMobile && mobileExpanded) &&
            <Box sx={{ flex: { xs: '0 0 auto', md: 1 }, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
            {
              StateManager.authed &&
              <NotificationTray />
            }
            {
              StateManager.authed &&
              <IconButton
                color="inherit"
                onClick={hendleOpen}
                size="large"
                aria-label={phoneUnreadCount > 0 ? `Menu, ${phoneUnreadCount} unread texts` : 'Menu'}
              >
                <Badge
                  badgeContent={phoneUnreadCount}
                  color="error"
                  invisible={!phoneUnreadCount}
                  sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}
                >
                  <MenuIcon />
                </Badge>
              </IconButton>
            }
            </Box>
          }
          <Menu
            id="menu"
            anchorEl={anchor}
            elevation={3}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            open={Boolean(anchor)}
            onClose={handleClose}
            PaperProps={{
              className: classes.menuPaper,
            }}
          >
            <MenuItem 
              className={classes.homeMenuItem}
              onClick={() => navigate('/')}
            >
              <ListItemIcon className={classes.menuItemIcon}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Home" />
            </MenuItem>

            {renderMenuSections(StateManager, navigate, handleAction, classes, { newServiceOrders: newSOCount, newLeads: newLeadsCount, leadFollowups: leadFollowupCount, phoneUnread: phoneUnreadCount })}
          </Menu>
        </Toolbar>
        <ResultsList results={results} selected={selected} onSelect={clearSearch}/>
      </AppBar>
      <div className={classes.appBarSpacer} />

      <AddCarDialog 
        open={isAddCarDialogOpen}
        onClose={() => setIsAddCarDialogOpen(false)}
      />

      <AddServiceOrderDialog
        open={isAddServiceOrderDialogOpen}
        onClose={() => setIsAddServiceOrderDialogOpen(false)}
        onCreated={(orderId) => StateManager.openServiceOrderModal(orderId)}
      />

      <AddCustomerDialog
        open={isAddCustomerDialogOpen}
        onClose={() => setIsAddCustomerDialogOpen(false)}
      />

      <AddLeadDialog
        open={isAddLeadDialogOpen}
        onClose={() => setIsAddLeadDialogOpen(false)}
        onSuccess={(leadId) => {
          console.log('Lead added successfully:', leadId);
          // Optionally refresh data or navigate to leads page
        }}
      />

      <BulkPurchaseModal
        open={isBulkPurchaseModalOpen}
        onClose={() => setIsBulkPurchaseModalOpen(false)}
      />

      <BulkIncentiveModal
        open={isBulkIncentiveModalOpen}
        onClose={() => setIsBulkIncentiveModalOpen(false)}
      />

      <CreateLabelDialog
        open={isShippingLabelDialogOpen}
        onClose={() => setIsShippingLabelDialogOpen(false)}
      />
    </>;
  }

  const drawerWidth = 240;

  const useStyles = makeStyles((theme) => ({
    toolbar: {
      paddingRight: 24, // keep right padding when drawer closed
    },
    logo:{
      width: 124,
      margin: 10,
      display: 'block',
      [theme.breakpoints.down('sm')]: {
        width: 84,
        margin: 6,
      },
    },
    appBar: {
      zIndex: theme.zIndex.drawer + 1,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
    appBarSpacer: theme.mixins.toolbar,
    appBarShift: {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    title: {
      flexGrow: 1,
    },
    appBarSpacer: theme.mixins.toolbar,
    menuPaper: {
      width: 320,
      maxHeight: 'calc(100vh - 100px)',
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '4px',
      },
    },
    menuSection: {
      padding: theme.spacing(1, 0),
    },
    menuSectionTitle: {
      padding: theme.spacing(1, 2),
      color: theme.palette.text.secondary,
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      backgroundColor: theme.palette.action.hover,
    },
    menuItem: {
      padding: theme.spacing(1, 2),
      minHeight: 48,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    menuItemIcon: {
      minWidth: 40,
      color: 'inherit',
    },
    menuItemText: {
      fontSize: '0.875rem',
    },
    divider: {
      margin: theme.spacing(1, 0),
    },
    homeMenuItem: {
      padding: theme.spacing(1.5, 2),
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  }));

  const getLabel = (hit, type) => {
    const highlights = hit._highlightResult
    if(type === "cars") {
      const stock = highlights.stock?.value || "";
      const car = (highlights.year?.value || "") + " " + (highlights.make?.value || "") + " " + (highlights.model?.value || "")
      return `${stock} - ${car}`;
    }
    if(type === "customers"){
      const name = (highlights.first_name?.value || "") + " " + (highlights.last_name?.value || "")
      const address = (highlights.address1?.value || "") + " " + (highlights.state?.value || "") + " " + (highlights.zip?.value || "")
      return `${name} - ${address}`;
    }
    if(type === "orders"){
      const id = highlights.id?.value || hit.id || hit.objectID || "";
      const carTitle = highlights.car_title?.value || hit.car_title || "";
      return carTitle ? `${id} — ${carTitle}` : `Order: ${id}`;
    }
  }

  const getAction = (hit, type) => {
    let action = () => null
    if(type === "cars") {
      action = (event) => {
        if (event?.metaKey || event?.ctrlKey) {
          const params = new URLSearchParams(window.location.search);
          params.set('car', hit.stock);
          window.open(`${window.location.pathname}?${params.toString()}`, '_blank');
          return;
        }
        StateManager.openCarModal(hit.stock);
      }
    }
    if(type === "customers"){
      action = (event) => {
        if (event?.metaKey || event?.ctrlKey) {
          const params = new URLSearchParams(window.location.search);
          params.set('customer', hit.objectID);
          window.open(`${window.location.pathname}?${params.toString()}`, '_blank');
          return;
        }
        StateManager.openCustomerModal(hit.objectID);
      }
    }
    if(type === "orders"){
      action = (event) => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('so', hit.objectID);
        if (event?.metaKey || event?.ctrlKey) {
          window.open(`${window.location.pathname}?${searchParams.toString()}`, '_blank');
          return;
        }
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
      }
    }
    return action
  }